// src/pages/landing/ExploreAssessments.js

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  FaArrowLeft,
  FaMagnifyingGlass,
  FaClock,
  FaListCheck,
  FaArrowRight,
} from "react-icons/fa6";

import {
  getPublicAssessments,
  validateProjectCodes,
} from "../../api/project/projectApi";

import TermsAndPolicyFooter from "../../components/common/PrivacyPolicy/CompanyTermsAndPolicy/TermsAndPolicyFooter";

// Check whether an assessment is currently available.
const isLive = (assessment) => {
  const now = Date.now();

  const start = assessment.start_date
    ? new Date(assessment.start_date).getTime()
    : null;

  const end = assessment.end_date
    ? new Date(assessment.end_date).getTime()
    : null;

  // Invalid dates should not incorrectly hide an assessment.
  if (start !== null && !Number.isNaN(start) && now < start) {
    return false;
  }

  if (end !== null && !Number.isNaN(end) && now > end) {
    return false;
  }

  if (assessment.is_active === false) {
    return false;
  }

  if (
    String(assessment.assessment_status || "").toLowerCase() === "stopped"
  ) {
    return false;
  }

  return true;
};

// Format dates for display.
const formatDate = (date) => {
  if (!date) return null;

  const parsedDate = new Date(date);

  if (Number.isNaN(parsedDate.getTime())) {
    return null;
  }

  return parsedDate.toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const ExploreAssessments = () => {
  const navigate = useNavigate();

  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");

  const [startingId, setStartingId] = useState(null);
  const [startError, setStartError] = useState("");

  // Fetch public assessments.
  useEffect(() => {
    let cancelled = false;

    const fetchAssessments = async () => {
      setLoading(true);
      setError("");

      try {
        console.log("Fetching public assessments...");

        const response = await getPublicAssessments();

        console.log(
          "Public assessments API response:",
          response?.status,
          response?.data
        );

        const responseData = response?.data;

        // Handle the response format returned by the backend.
        let list = [];

        if (Array.isArray(responseData)) {
          list = responseData;
        } else if (Array.isArray(responseData?.data)) {
          list = responseData.data;
        } else if (Array.isArray(responseData?.assessments)) {
          list = responseData.assessments;
        } else if (Array.isArray(responseData?.results)) {
          list = responseData.results;
        } else {
          throw new Error(
            responseData?.message ||
              "The server returned an unexpected assessment format."
          );
        }

        // Normalize assessment fields without changing the original data.
        const normalizedAssessments = list.map((assessment) => ({
          ...assessment,

          project_name:
            assessment.project_name ||
            assessment.assessment_name ||
            "Untitled assessment",

          project_code:
            assessment.project_code ||
            assessment.question_code ||
            "",

          description: assessment.description || "",

          assessment_type:
            assessment.assessment_type || "",

          total_questions:
            assessment.total_questions ?? null,

          duration:
            assessment.duration ?? null,

          start_date:
            assessment.start_date ?? null,

          end_date:
            assessment.end_date ?? null,
        }));

        const liveAssessments = normalizedAssessments.filter(isLive);

        if (!cancelled) {
          setAssessments(liveAssessments);
        }
      } catch (err) {
        console.error("Error fetching public assessments:", err);

        console.error("API error response:", err?.response?.data);
        console.error("API status:", err?.response?.status);
        console.error("API URL:", err?.config?.url);

        if (!cancelled) {
          setError(
            err?.response?.data?.message ||
              err?.message ||
              "Couldn't load assessments. Please try again."
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchAssessments();

    return () => {
      cancelled = true;
    };
  }, []);

  // Search assessments by name, description, or assessment type.
  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();

    if (!query) {
      return assessments;
    }

    return assessments.filter((assessment) => {
      const searchableFields = [
        assessment.project_name,
        assessment.description,
        assessment.assessment_type,
        assessment.project_code,
      ];

      return searchableFields
        .filter(Boolean)
        .some((value) =>
          String(value).toLowerCase().includes(query)
        );
    });
  }, [assessments, search]);

  // Start an assessment using the existing project-code validation flow.
  const handleStart = async (assessment) => {
    if (!assessment.project_code) {
      setStartError(
        "This assessment does not have a valid assessment code."
      );
      return;
    }

    setStartError("");
    setStartingId(assessment.project_code);

    try {
      const response = await validateProjectCodes(
        assessment.project_code
      );

      const { status, message, project_id } = response.data;

      if (status) {
        navigate("/learner-details", {
          state: {
            projectId: project_id,
            projectCode: assessment.project_code,
          },
        });
      } else {
        setStartError(
          message || "This assessment can't be started right now."
        );
      }
    } catch (err) {
      console.error("Error starting assessment:", err);

      setStartError(
        err?.response?.data?.message ||
          "Something went wrong. Please try again."
      );
    } finally {
      setStartingId(null);
    }
  };

  const handleRetry = () => {
    window.location.reload();
  };

  return (
    <div className="flex flex-col w-full min-h-screen overflow-x-hidden">
      {/* Hero section */}
      <div className="w-full bg-gradient-to-l from-indigo-950 to-indigo-800 text-white">
        <div className="w-11/12 md:w-9/12 mx-auto py-10 md:py-14">
          <button
            type="button"
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-sm text-indigo-200 hover:text-white transition"
          >
            <FaArrowLeft />
            Back to home
          </button>

          <h1 className="text-3xl md:text-5xl font-extrabold mt-6">
            Explore Assessments
          </h1>

          <p className="text-indigo-200 mt-3 max-w-2xl">
            Public assessments that are live right now. Pick one and
            start, no code needed.
          </p>

          {/* Search */}
          <div className="relative mt-8 max-w-2xl">
            <FaMagnifyingGlass className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />

            <input
              type="text"
              placeholder="Search by name, topic or type"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              aria-label="Search assessments"
              className="w-full pl-11 pr-4 py-3 rounded-xl text-gray-800 shadow focus:outline-none focus:ring-2 focus:ring-purple-400"
            />
          </div>
        </div>
      </div>

      {/* Assessment list */}
      <div className="w-11/12 md:w-9/12 mx-auto py-10 md:py-14 flex-1">
        {/* Assessment count */}
        {!loading && !error && (
          <p className="text-sm text-gray-500 mb-6">
            {filtered.length} live assessment
            {filtered.length !== 1 ? "s" : ""}
          </p>
        )}

        {/* Start assessment error */}
        {startError && (
          <div
            role="alert"
            className="mb-6 px-4 py-3 rounded-xl bg-red-50 border border-red-100 text-red-600 text-sm"
          >
            {startError}
          </div>
        )}

        {/* Loading skeletons */}
        {loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 6 }).map((_, index) => (
              <div
                key={index}
                className="h-56 rounded-2xl bg-gray-100 animate-pulse"
              >
                <div className="p-6 space-y-4">
                  <div className="h-5 bg-gray-200 rounded w-3/4" />
                  <div className="h-4 bg-gray-200 rounded w-full" />
                  <div className="h-4 bg-gray-200 rounded w-2/3" />
                  <div className="h-10 bg-gray-200 rounded-xl mt-6" />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* API error */}
        {!loading && error && (
          <div className="text-center py-16">
            <div className="mx-auto mb-4 w-14 h-14 rounded-full bg-red-50 flex items-center justify-center text-red-500 text-2xl">
              !
            </div>

            <h2 className="text-lg font-bold text-gray-800">
              Unable to load assessments
            </h2>

            <p className="text-red-500 mt-2 text-sm break-words">
              {error}
            </p>

            <button
              type="button"
              onClick={handleRetry}
              className="mt-6 px-6 py-3 rounded-xl bg-[#36369f] text-white font-semibold hover:opacity-90 transition"
            >
              Try Again
            </button>
          </div>
        )}

        {/* Empty state */}
        {!loading && !error && filtered.length === 0 && (
          <div className="text-center py-16">
            <div className="mx-auto mb-4 w-16 h-16 rounded-full bg-indigo-50 flex items-center justify-center">
              <FaMagnifyingGlass className="text-2xl text-[#36369f]" />
            </div>

            <h2 className="text-lg font-bold text-gray-800">
              {search
                ? "No matching assessments"
                : "No public assessments available"}
            </h2>

            <p className="text-gray-500 mt-2">
              {search
                ? "Try searching with a different name, topic or assessment code."
                : "There are no public assessments available right now."}
            </p>

            {search ? (
              <button
                type="button"
                onClick={() => setSearch("")}
                className="mt-4 text-[#36369f] font-semibold hover:underline"
              >
                Clear search
              </button>
            ) : (
              <button
                type="button"
                onClick={() => navigate("/")}
                className="mt-4 text-[#36369f] font-semibold hover:underline"
              >
                Have a code? Enter it on the home page
              </button>
            )}
          </div>
        )}

        {/* Assessment cards */}
        {!loading && !error && filtered.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 items-stretch">
            {filtered.map((assessment, index) => (
              <div
                key={
                  assessment.project_code ||
                  assessment.project_id ||
                  index
                }
                className="group relative h-full"
              >
                {/* Hover glow */}
                <div className="absolute inset-0 bg-gradient-to-r from-purple-400 to-blue-400 opacity-0 group-hover:opacity-20 blur-xl rounded-2xl transition-all duration-300" />

                <div className="relative flex flex-col justify-between h-full bg-white border border-gray-100 rounded-2xl p-6 shadow-md hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                  <div>
                    {/* Title and status */}
                    <div className="flex items-start justify-between gap-3">
                      <h3 className="text-lg font-bold text-gray-800 leading-snug break-words">
                        {assessment.project_name}
                      </h3>

                      <span className="flex items-center gap-1.5 shrink-0 px-2.5 py-1 text-xs font-semibold text-green-700 bg-green-100 rounded-full">
                        <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                        Live
                      </span>
                    </div>

                    {/* Description */}
                    {assessment.description && (
                      <p className="text-sm text-gray-600 mt-2 line-clamp-3">
                        {assessment.description}
                      </p>
                    )}

                    <div className="flex flex-wrap gap-x-4 gap-y-2 mt-4 text-xs text-gray-500">
                      {assessment.assessment_type && (
                        <span className="px-2 py-1 rounded-full bg-indigo-50 text-indigo-700 font-medium">
                          {assessment.assessment_type}
                        </span>
                      )}

                      {assessment.total_questions != null && (
                        <span className="flex items-center gap-1">
                          <FaListCheck />
                          {assessment.total_questions} questions
                        </span>
                      )}

                      {assessment.duration != null && (
                        <span className="flex items-center gap-1">
                          <FaClock />
                          {assessment.duration} min
                        </span>
                      )}

                      {assessment.end_date && (
                        <span>
                          Ends {formatDate(assessment.end_date)}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Start button */}
                  <button
                    type="button"
                    onClick={() => handleStart(assessment)}
                    disabled={startingId !== null}
                    className="mt-6 flex items-center justify-center gap-2 w-full py-3 rounded-xl text-white font-semibold bg-gradient-to-l from-indigo-950 to-indigo-800 hover:scale-[1.02] transition disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {startingId === assessment.project_code ? (
                      "Starting..."
                    ) : (
                      <>
                        Start Assessment
                        <FaArrowRight />
                      </>
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <TermsAndPolicyFooter />
    </div>
  );
};

export default ExploreAssessments;